/**
 * ManualAssessmentHistory Component
 * Displays manual assessment workflow history table with filters and terminate action.
 * Filters (status, initiatedBy, initiatedDate) are sent as query params to the API.
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { flushSync } from "react-dom";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import CircularProgress from "@mui/material/CircularProgress";
import LinearProgress from "@mui/material/LinearProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import InputAdornment from "@mui/material/InputAdornment";
import Snackbar from "@mui/material/Snackbar";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import {
  DataGridPremium,
  GridColDef,
  GridSortModel,
} from "@mui/x-data-grid-premium";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appDatePickerSlots } from "../../shared/datePickerSlots";
import dayjs, { Dayjs } from "dayjs";
import { apiClient } from "../../../services/apiClient";
import { useHasPrivilege } from "../../../hooks/useHasPrivilege";
import { MANUAL_IA_PDF_PRIVILEGES } from "../../../services/permissionsService";
import {
  buildSyncManualAssessmentWorkflowStatusParams,
  getManualAssessmentWorkflows,
  getManualWorkflowFilterOptions,
  manualWorkflowListSortParamFromSortModel,
  syncManualAssessmentWorkflowStatus,
} from "../../../services/manualAssessmentService";
import {
  extractProcessAssociationIdFromListItem,
  getWorkflowUuidFromListItem,
} from "../../../utils/enrichManualWorkflowProcessAssociation";
import { ManualAssessmentProcessNameCell } from "../../../utils/manualAssessmentProcessNameLink";
import { withoutBackdropClose } from "../../../utils/withoutBackdropClose";
import { ShareManualIaPdfDialog } from "../ShareManualIaPdfDialog";
import { PDFViewer } from "../PDFViewer";
import {
  resolveManualWorkflowAttachmentS3Uri,
  resolveManualWorkflowPreviousFormsUri,
  type ManualIaPdfShareTarget,
} from "../../../utils/manualAssessmentReportShare";
import shareIcon from "../../../assets/share.svg";
import eyeIcon from "../../../assets/eye.svg";
import downloadIcon from "../../../assets/download.svg";
import { AI_IMPACT_ACTION_BUTTON_SX } from "../../AssessmentCenterPage/assessmentCenterColumns";
import { useManualIaPdfRowActions } from "../../AssessmentCenterPage/useManualIaPdfRowActions";

const PROCESS_NAME_DEBOUNCE_MS = 500;

/** Fixed grid viewport — body scrolls inside; not tied to page size. */
const MANUAL_ASSESSMENT_HISTORY_GRID_HEIGHT = 480;

interface ManualWorkflowRow {
  id: string;
  workflowUuid: string;
  processName: string;
  processAssociationId: string;
  initiatedBy: string;
  initiatedDate: string;
  status: string;
  regulationId: string;
  regulationVersion: string;
  attachmentS3Uri: string;
  assessmentLink: string;
  isViewingWholeWorkflow?: boolean;
  isOpeningWholeWorkflow?: boolean;
}

interface ManualAssessmentHistoryProps {
  regulationId: string;
  regulationVersion: string;
  onViewDetails?: (workflowUuid: string) => void;
  onRefreshNeeded?: () => void;
  onOpenWholeWorkflow?: (uri: string) => void;
  /** Currently open half-page P&T URI (null when the pane is closed). */
  activeWholeWorkflowUrl?: string | null;
}

export const MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS =
  "manual-ia-history-row--active";
export const MANUAL_IA_HISTORY_ROW_OPENING_CLASS =
  "manual-ia-history-row--opening";

export const ManualAssessmentHistory: React.FC<ManualAssessmentHistoryProps> = ({
  regulationId: rawRegulationId,
  regulationVersion,
  onViewDetails,
  onRefreshNeeded,
  onOpenWholeWorkflow,
  activeWholeWorkflowUrl = null,
}) => {
  const regulationId = rawRegulationId && !rawRegulationId.startsWith("INS")
    ? `INS${rawRegulationId}`
    : rawRegulationId;
  const canManageManualAssessment = useHasPrivilege("gri.assessment.manual.process.manage");
  const canTerminateManualAssessment = useHasPrivilege("gri.assessment.manual.process.terminate");
  const canViewManualIaPdf = useHasPrivilege(MANUAL_IA_PDF_PRIVILEGES.view);
  const canDownloadManualIaPdf = useHasPrivilege(
    MANUAL_IA_PDF_PRIVILEGES.download,
  );
  const canShareManualIaPdf = useHasPrivilege(
    MANUAL_IA_PDF_PRIVILEGES.share,
  );
  /** Immediate value for the Process name input */
  const [processNameInput, setProcessNameInput] = useState("");
  /** Debounced value sent to the history API (see useEffect below) */
  const [debouncedProcessName, setDebouncedProcessName] = useState("");
  const [status, setStatus] = useState("");
  const [initiatedBy, setInitiatedBy] = useState("");
  const [initiatedDate, setInitiatedDate] = useState<Dayjs | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyData, setHistoryData] = useState<ManualWorkflowRow[]>([]);
  const [filterOptions, setFilterOptions] = useState<Record<string, { name: string; value: string }[]>>({});
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: "initiatedDate", sort: "desc" },
  ]);
  const [totalRows, setTotalRows] = useState(0);
  const sortChangedRef = useRef(false);
  const [terminatingId, setTerminatingId] = useState<string | null>(null);
  const [terminateDialog, setTerminateDialog] = useState<{ open: boolean; id: string; name: string }>({
    open: false, id: "", name: "",
  });
  const [shareTarget, setShareTarget] = useState<ManualIaPdfShareTarget | null>(
    null,
  );
  const {
    previewPdf,
    downloadPdf,
    downloadingFilePath,
    pdfDialogOpen,
    selectedPdfUrl,
    closePdfDialog,
  } = useManualIaPdfRowActions();
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [activeRowId, setActiveRowId] = useState<string | null>(null);
  const [openingRowId, setOpeningRowId] = useState<string | null>(null);
  const paneWasOpenRef = useRef(false);
  const urlWhenOpeningRef = useRef<string | null>(null);

  const handleProcessNameOpenStart = useCallback(
    (rowId: string) => {
      urlWhenOpeningRef.current = activeWholeWorkflowUrl;
      flushSync(() => {
        setOpeningRowId(rowId);
        setActiveRowId(null);
      });
    },
    [activeWholeWorkflowUrl],
  );

  const handleOpenPatProcessForRow = useCallback(
    (rowId: string, url: string) => {
      if (openingRowId !== rowId) {
        urlWhenOpeningRef.current = activeWholeWorkflowUrl;
        flushSync(() => {
          setOpeningRowId(rowId);
          setActiveRowId(null);
        });
      }
      onOpenWholeWorkflow?.(url);
      if (url === activeWholeWorkflowUrl) {
        setActiveRowId(rowId);
        setOpeningRowId(null);
      }
    },
    [onOpenWholeWorkflow, openingRowId, activeWholeWorkflowUrl],
  );

  useEffect(() => {
    if (activeWholeWorkflowUrl) {
      paneWasOpenRef.current = true;
      if (
        openingRowId &&
        activeWholeWorkflowUrl !== urlWhenOpeningRef.current
      ) {
        setActiveRowId(openingRowId);
        setOpeningRowId(null);
      }
      return;
    }
    if (paneWasOpenRef.current) {
      paneWasOpenRef.current = false;
      setActiveRowId(null);
      setOpeningRowId(null);
    }
  }, [activeWholeWorkflowUrl, openingRowId]);

  const handleStatusChange = (val: string) => {
    setStatus(val);
  };

  const handleInitiatedByChange = (val: string) => {
    setInitiatedBy(val);
  };

  const handleInitiatedDateChange = (val: Dayjs | null) => {
    setInitiatedDate(val);
  };

  const getStatusChipProps = (statusVal: string) => {
    const s = (statusVal || "").toLowerCase();
    const capitalized = statusVal.charAt(0).toUpperCase() + statusVal.slice(1).toLowerCase();
    
    if (s === "completed" || s === "success") {
      return { label: "Completed", color: "#dcfce7", textColor: "#166534" };
    }
    if (s === "in progress" || s === "running") {
      return { label: "In Progress", color: "#E7ECFC", textColor: "#295EF1" };
    }
    if (s === "not started" || s === "init") {
      return { label: "Not Started", color: "#FFF0B3", textColor: "#FF8B00" };
    }
    if (s === "terminated" || s === "cancelled") {
      return { label: "Terminated", color: "#f3f4f6", textColor: "#9ca3af" };
    }
    return { label: capitalized, color: "#f3f4f6", textColor: "#374151" };
  };

  useEffect(() => {
    const scope = buildSyncManualAssessmentWorkflowStatusParams(
      regulationId,
      regulationVersion,
    );
    if (!scope) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await getManualWorkflowFilterOptions({
          regulation_id: scope.regulation_id,
          regulation_version: scope.regulation_version,
        });
        if (cancelled) return;
        if (Array.isArray(list)) {
          const map: Record<string, { name: string; value: string }[]> = {};
          list.forEach((item) => {
            map[item.name] = Array.isArray(item.options)
              ? item.options.map((opt) => ({
                  name: opt.name,
                  value:
                    typeof opt.value === "string"
                      ? opt.value
                      : Array.isArray(opt.value)
                        ? opt.value.join(",")
                        : opt.name,
                }))
              : [];
          });
          setFilterOptions(map);
        }
      } catch (err) {
        console.error("Failed to fetch workflow filter options:", err);
      }
    })();
    return () => { cancelled = true; };
  }, [regulationId, regulationVersion]);

  // Debounce effect for process name input
  useEffect(() => {
    // Only run if input differs from the current debounced value
    if (processNameInput === debouncedProcessName) return;

    const t = window.setTimeout(() => {
      setDebouncedProcessName(processNameInput);
      // Reset to first page when search changes
      setPaginationModel((pm) => (pm.page === 0 ? pm : { ...pm, page: 0 }));
    }, PROCESS_NAME_DEBOUNCE_MS);

    return () => window.clearTimeout(t);
  }, [processNameInput, debouncedProcessName]);

  const fetchCountRef = useRef(0);

  const fetchHistory = useCallback(async () => {
    const scope = buildSyncManualAssessmentWorkflowStatusParams(
      regulationId,
      regulationVersion,
    );
    if (!scope) return;

    const currentFetchCount = ++fetchCountRef.current;

    try {
      setLoading(true);
      setError(null);

      // Status check (no need to block the whole UI if this fails)
      try {
        await syncManualAssessmentWorkflowStatus(scope);
      } catch (e) {
        console.warn("Status update failed, proceeding with history fetch", e);
      }

      const payload = await getManualAssessmentWorkflows({
        page: paginationModel.page + 1,
        page_size: paginationModel.pageSize,
        regulation_id: scope.regulation_id,
        regulation_version: scope.regulation_version,
        process_name: debouncedProcessName || undefined,
        status: status || undefined,
        initiated_by: initiatedBy || undefined,
        initiated_date: initiatedDate
          ? initiatedDate.format("YYYY-MM-DD")
          : undefined,
        sort: manualWorkflowListSortParamFromSortModel(sortModel),
      });

      if (currentFetchCount !== fetchCountRef.current) return;

      const rawData = payload.items;
      const total = payload.total;

      if (Array.isArray(rawData)) {
        const transformed: ManualWorkflowRow[] = rawData.map((item) => ({
          id: String(item.id ?? ""),
          workflowUuid:
            getWorkflowUuidFromListItem(item as Record<string, unknown>) ?? "",
          processName: item.process_name?.trim() || "-",
          processAssociationId: extractProcessAssociationIdFromListItem(
            item as Record<string, unknown>,
          ),
          initiatedBy: (item.initiated_by ?? "").trim() || "-",
          initiatedDate: item.initiated_at ?? "",
          status: item.status || "-",
          regulationId: item.regulation_id || scope.regulation_id,
          regulationVersion: item.regulation_version || scope.regulation_version,
          attachmentS3Uri: resolveManualWorkflowAttachmentS3Uri(
            item as Record<string, unknown>,
          ),
          assessmentLink: resolveManualWorkflowPreviousFormsUri(
            item as Record<string, unknown>,
          ),
        }));

        setHistoryData(transformed);
        setTotalRows(total);
      } else {
        setHistoryData([]);
        // Preserve totalRows so server pagination does not snap back to page 0.
      }
    } catch (err) {
      if (currentFetchCount !== fetchCountRef.current) return;
      console.error("Failed to fetch manual assessment history:", err);
      setError("Failed to load history data");
      // Preserve totalRows on transport/API errors.
    } finally {
      if (currentFetchCount === fetchCountRef.current) {
        setLoading(false);
      }
    }
  }, [
    regulationId,
    regulationVersion,
    debouncedProcessName,
    status,
    initiatedBy,
    initiatedDate,
    paginationModel,
    sortModel,
  ]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const openTerminateDialog = useCallback((row: ManualWorkflowRow) => {
    setTerminateDialog({ open: true, id: row.id, name: row.processName });
  }, []);

  const closeTerminateDialog = useCallback(() => {
    setTerminateDialog({ open: false, id: "", name: "" });
  }, []);

  const handleTerminateConfirm = async () => {
    const { id } = terminateDialog;
    if (!id) return;
    closeTerminateDialog();

    try {
      setTerminatingId(id);
      const response = await apiClient.delete("/manual-assessment/workflows", {
        data: { workflowIds: [id] },
      });
      if (response.status === "success") {
        setSnackbar({ open: true, message: "Workflow terminated successfully.", severity: "success" });
        fetchHistory();
        onRefreshNeeded?.();
      } else {
        setSnackbar({ open: true, message: response.message || "Failed to terminate workflow", severity: "error" });
      }
    } catch (err: any) {
      console.error("Error terminating workflow:", err);
      const msg = err?.response?.data?.message ?? err?.message ?? "An error occurred while terminating the workflow";
      setSnackbar({ open: true, message: msg, severity: "error" });
    } finally {
      setTerminatingId(null);
    }
  };

  const columns: GridColDef<ManualWorkflowRow>[] = useMemo(() => [
    {
      field: "processName",
      headerName: "Process name",
      flex: 1.5,
      minWidth: 200,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => (
        <ManualAssessmentProcessNameCell
          processName={String(params.value ?? "—")}
          workflowUuid={params.row.workflowUuid ?? ""}
          processAssociationId={params.row.processAssociationId ?? ""}
          status={params.row.status ?? ""}
          canManageManualAssessment={canManageManualAssessment}
          onOpenPatProcess={
            onOpenWholeWorkflow
              ? (url) => handleOpenPatProcessForRow(params.row.id, url)
              : undefined
          }
          onOpenStart={
            onOpenWholeWorkflow
              ? () => handleProcessNameOpenStart(params.row.id)
              : undefined
          }
          isOpening={Boolean(params.row.isOpeningWholeWorkflow)}
        />
      ),
    },
    {
      field: "initiatedBy",
      headerName: "Initiated by",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "initiatedDate",
      headerName: "Initiated date",
      flex: 1,
      minWidth: 150,
      sortable: true,
      valueFormatter: (value) => (value ? dayjs(value).format("DD-MMM-YYYY") : "-"),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const { label, color, textColor } = getStatusChipProps(params.value);
        return (
          <Chip
            label={label}
            size="small"
            sx={{
              backgroundColor: color,
              color: textColor,
              fontWeight: 500,
              fontSize: "0.75rem",
              height: "24px",
            }}
          />
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      width: 200,
      sortable: false,
      renderCell: (params) => {
        const s = params.row.status.toLowerCase();
        const canTerminate =
          canTerminateManualAssessment &&
          (s === "not started" || s === "init" || s === "in progress" || s === "running");
        const hasPdf = Boolean(params.row.attachmentS3Uri);
        const canView = hasPdf && canViewManualIaPdf;
        const canDownload = hasPdf && canDownloadManualIaPdf;
        const canShare = hasPdf && canShareManualIaPdf;
        if (!canView && !canDownload && !canShare && !canTerminate) {
          return (
            <Tooltip
              title={
                canTerminateManualAssessment
                  ? "This process cannot be terminated"
                  : "No permission"
              }
            >
              <span>-</span>
            </Tooltip>
          );
        }
        const downloading = downloadingFilePath === params.row.attachmentS3Uri;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {canView ? (
              <Tooltip title="View">
                <span>
                  <IconButton
                    size="small"
                    aria-label="View"
                    disabled={terminatingId === params.row.id || downloading}
                    onClick={() => {
                      void previewPdf(params.row.attachmentS3Uri);
                    }}
                    sx={AI_IMPACT_ACTION_BUTTON_SX}
                  >
                    <Box
                      component="img"
                      src={eyeIcon}
                      alt=""
                      sx={{ width: 16, height: 16, display: "block" }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canDownload ? (
              <Tooltip title={downloading ? "Downloading…" : "Download"}>
                <span>
                  <IconButton
                    size="small"
                    aria-label="Download"
                    disabled={terminatingId === params.row.id || downloading}
                    onClick={() => {
                      void downloadPdf(params.row.attachmentS3Uri);
                    }}
                    sx={AI_IMPACT_ACTION_BUTTON_SX}
                  >
                    {downloading ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <Box
                        component="img"
                        src={downloadIcon}
                        alt=""
                        sx={{ width: 16, height: 16, display: "block" }}
                      />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canShare ? (
              <Tooltip title="Share">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Share"
                    disabled={terminatingId === params.row.id}
                    onClick={() =>
                      setShareTarget({
                        regulationId: params.row.regulationId,
                        regulationVersion: params.row.regulationVersion,
                        attachmentS3Uri: params.row.attachmentS3Uri,
                        assessmentLink: params.row.assessmentLink,
                        source:
                          new URLSearchParams(window.location.search)
                            .get("source")
                            ?.trim() || undefined,
                      })
                    }
                    sx={AI_IMPACT_ACTION_BUTTON_SX}
                  >
                    <Box
                      component="img"
                      src={shareIcon}
                      alt=""
                      sx={{ width: 16, height: 16, display: "block" }}
                    />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            {canTerminate ? (
              <Tooltip title="Terminate">
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    aria-label="Terminate"
                    disabled={terminatingId === params.row.id}
                    onClick={() => openTerminateDialog(params.row)}
                  >
                    {terminatingId === params.row.id ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      <RemoveCircleOutlineIcon fontSize="small" />
                    )}
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
          </Box>
        );
      },
    },
  ], [
    canManageManualAssessment,
    onOpenWholeWorkflow,
    handleOpenPatProcessForRow,
    handleProcessNameOpenStart,
    canTerminateManualAssessment,
    canViewManualIaPdf,
    canDownloadManualIaPdf,
    canShareManualIaPdf,
    terminatingId,
    downloadingFilePath,
    openTerminateDialog,
    previewPdf,
    downloadPdf,
  ]);

  const historyGridRows = useMemo(
    () =>
      historyData.map((row) => ({
        ...row,
        isViewingWholeWorkflow: row.id === activeRowId,
        isOpeningWholeWorkflow: row.id === openingRowId,
      })),
    [historyData, activeRowId, openingRowId],
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filters Row */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "nowrap", alignItems: "center" }}>
        <TextField
          size="small"
          label="Process name"
          placeholder="Search process name"
          value={processNameInput}
          onChange={(e) => {
            setProcessNameInput(e.target.value);
          }}
          sx={{ minWidth: 200 }}
          autoComplete="off"
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: processNameInput ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => {
                  setProcessNameInput("");
                }} edge="end">
                  <CloseIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <TextField
          select
          size="small"
          label="Status"
          value={status}
          onChange={(e) => handleStatusChange(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {(filterOptions.status ?? []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.name}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Initiated by"
          value={initiatedBy}
          onChange={(e) => handleInitiatedByChange(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          {(filterOptions.initiated_by ?? []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.name}
            </MenuItem>
          ))}
        </TextField>

        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="Initiated date"
            value={initiatedDate}
            format="DD-MMM-YYYY"
            onChange={(val) => handleInitiatedDateChange(val)}
            slots={appDatePickerSlots}
            slotProps={{
              textField: {
                size: "small",
                sx: { minWidth: 200 },
              },
              field: { clearable: true, onClear: () => handleInitiatedDateChange(null) },
            }}
          />
        </LocalizationProvider>
      </Box>

      {/* Table — fixed height with internal scroll */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          height: MANUAL_ASSESSMENT_HISTORY_GRID_HEIGHT,
          minHeight: MANUAL_ASSESSMENT_HISTORY_GRID_HEIGHT,
          maxHeight: MANUAL_ASSESSMENT_HISTORY_GRID_HEIGHT,
        }}
      >
        {openingRowId ? (
          <LinearProgress
            aria-label="Opening process"
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 2,
              borderRadius: "8px 8px 0 0",
            }}
          />
        ) : null}
        <DataGridPremium
          rows={historyGridRows}
          columns={columns}
          loading={loading}
          pagination
          paginationMode="server"
          rowCount={totalRows}
          paginationModel={paginationModel}
          onPaginationModelChange={(newModel) => {
            setLoading(true);
            if (sortChangedRef.current) {
              sortChangedRef.current = false;
              if (newModel.page === 0 && paginationModel.page !== 0) {
                setPaginationModel((prev) => ({
                  ...prev,
                  pageSize: newModel.pageSize,
                }));
                return;
              }
            }
            setPaginationModel((oldModel) => {
              if (
                oldModel.page === newModel.page &&
                oldModel.pageSize === newModel.pageSize
              ) {
                return oldModel;
              }
              return newModel;
            });
          }}
          sortingMode="server"
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            setLoading(true);
            sortChangedRef.current = true;
            setSortModel(newSortModel);
          }}
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[10, 20, 50, 100]}
          getRowHeight={() => "auto"}
          getRowClassName={(params) => {
            if (params.row.isOpeningWholeWorkflow) {
              return MANUAL_IA_HISTORY_ROW_OPENING_CLASS;
            }
            return params.row.isViewingWholeWorkflow
              ? MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS
              : "";
          }}
          sx={{
            height: "100%",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            "& .MuiDataGrid-main": {
              overflow: "hidden",
            },
            "& .MuiDataGrid-virtualScroller": {
              overflow: "auto",
            },
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: "#f9fafb",
              borderBottom: "1px solid #e5e7eb",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              fontSize: (theme) => theme.typography.body2.fontSize,
              color: "#374151",
            },
            "& .MuiDataGrid-cell": {
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #f3f4f6",
              fontSize: (theme) => theme.typography.body2.fontSize,
              py: 1.5,
              lineHeight: "unset !important",
              maxHeight: "none !important",
              whiteSpace: "normal",
              "& a.MuiLink-root, & button.MuiLink-root": {
                color: "#2563eb",
                textDecorationColor: "#2563eb",
              },
            },
            "& .MuiDataGrid-cell[data-field='processName'] .manual-ia-process-name": {
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              width: "100%",
              textAlign: "left",
            },
            "& .MuiDataGrid-cell[data-field='processName'] .MuiLink-root": {
              minWidth: 0,
              textAlign: "left",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_OPENING_CLASS}`]: {
              backgroundColor: "#F8FAFC",
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS}`]: {
              backgroundColor: "#EFF6FF",
              boxShadow: "inset 3px 0 0 #2563eb",
              fontWeight: 700,
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS} .MuiDataGrid-cell`]: {
              fontWeight: 700,
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS} .MuiLink-root`]: {
              fontWeight: 700,
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS} .MuiChip-label`]: {
              fontWeight: 700,
            },
            [`& .MuiDataGrid-row.${MANUAL_IA_HISTORY_ROW_ACTIVE_CLASS}:hover`]: {
              backgroundColor: "#DBEAFE",
            },
            "& .MuiDataGrid-footerContainer": {
              borderTop: "1px solid #e5e7eb",
            },
          }}
        />
      </Box>

      <PDFViewer
        open={pdfDialogOpen}
        pdfUrl={selectedPdfUrl}
        title="PDF Preview"
        onClose={closePdfDialog}
      />

      {shareTarget ? (
        <ShareManualIaPdfDialog
          open
          variant="regulationDetails"
          target={shareTarget}
          onClose={() => setShareTarget(null)}
        />
      ) : null}

      {/* Terminate Confirmation Dialog */}
      <Dialog
        open={terminateDialog.open}
        onClose={withoutBackdropClose(closeTerminateDialog)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "12px" } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography component="span" variant="subtitle1" sx={{ fontWeight: 600 }}>
            Terminate This Manual Assessment Process?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <Typography variant="body2" sx={{ color: "#6b7280" }}>
            Are you sure you want to terminate the Manual Assessment process &quot;
            <strong>{terminateDialog.name}</strong>&quot;?
            Once terminated, it cannot be reactivated.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="outlined" onClick={closeTerminateDialog}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleTerminateConfirm}
            sx={{ textTransform: "none" }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success / Error Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
